import StrappService from '@/services/StrappService'
import { SimStatus } from '../types'

/**
 * Provider pour les SIM Strapp
 * Implémente l'interface ISimProvider
 */
const StrappSimProvider = {
  name: 'strapp',

  /**
   * Mappe les états Strapp (state) vers les valeurs standards
   * @param {string} strappState - État Strapp (ex: "🟢 Abonnement actif")
   * @returns {string} Valeur standardisée
   */
  mapStrappState(strappState) {
    if (!strappState) return SimStatus.INACTIVE

    // Retirer les emojis en prenant le texte après le premier espace
    const parts = strappState.split(/\s+/)
    const cleanState = parts.length > 1 ? parts.slice(1).join(' ') : strappState

    const stateMapping = {
      'Non activée': SimStatus.INACTIVE,
      'Portabilité demandée': SimStatus.PENDING,
      'Portabilité validée': SimStatus.PENDING,
      'Activation demandée': SimStatus.PENDING,
      'Activation planifiée': SimStatus.PENDING,
      'Abonnement actif': SimStatus.ACTIVE,
      'Changement de forfait planifié': SimStatus.ACTIVE,
      'En pause': SimStatus.SUSPENDED,
      'Résiliation demandée': SimStatus.TERMINATED,
      'Résiliation planifiée': SimStatus.TERMINATED,
      'Résiliée': SimStatus.TERMINATED
    }

    return stateMapping[cleanState] || SimStatus.INACTIVE
  },

  /**
   * Mappe les statuts logistiques Strapp (status) vers les valeurs standards
   * @param {string} strappStatus - Statut Strapp (ex: "✅ Remis à l'utilisateur")
   * @returns {string} Valeur standardisée
   */
  mapStrappStatus(strappStatus) {
    if (!strappStatus) return SimStatus.INACTIVE

    // Retirer les emojis en prenant le texte après le premier espace
    const parts = strappStatus.split(/\s+/)
    const cleanStatus = parts.length > 1 ? parts.slice(1).join(' ') : strappStatus

    const statusMapping = {
      'Annulé': SimStatus.TERMINATED,
      'En commande': SimStatus.PENDING,
      'En livraison': SimStatus.PENDING,
      'En stock': SimStatus.INACTIVE,
      'En stock (réservée)': SimStatus.INACTIVE,
      'Remis à l\'utilisateur': SimStatus.ACTIVE,
      'En attente de restitution': SimStatus.ACTIVE,
      'Restituée à Strapp': SimStatus.TERMINATED,
      'Perdue/Volée': SimStatus.TERMINATED,
      'Recyclée': SimStatus.TERMINATED
    }

    return statusMapping[cleanStatus] || SimStatus.INACTIVE
  },

  /**
   * Normalise une SIM Strapp vers le format commun
   * @param {Object} strappSim - SIM brute de Strapp
   * @returns {Sim}
   */
  normalizeSim(strappSim) {
    // Utiliser state comme status principal (cycle de vie > logistique)
    const normalizedStatus = this.mapStrappState(strappSim.state)

    return {
      iccid: strappSim.iccid,
      provider: 'strapp',
      msisdn: strappSim.phoneNumber || strappSim.msisdn,
      status: normalizedStatus,
      operator: strappSim.operator,
      usage: strappSim.usage ? {
        totalDataMo: strappSim.usage.totalDataMo || 0,
        periodDataMo: strappSim.usage.periodDataMo || 0
      } : null,
      quotaMo: strappSim.quotaMo || 0,
      assignedDevice: strappSim.device?.imei || null,
      activationDate: strappSim.activationDate ? new Date(strappSim.activationDate) : null,
      lastSync: new Date(),
      raw: strappSim
    }
  },

  /**
   * Récupère une liste de SIMs Strapp
   * @param {SimFilters} filters - Filtres à appliquer
   * @returns {Promise<PaginatedResult>}
   */
  getSims(filters = {}) {
    const strappFilters = {
      iccid: filters.iccid,
      phoneNumber: filters.msisdn,
      user: filters.userId,
      status: filters.statuses ? filters.statuses[0] : undefined,
      populate: 'device,subscription',
      limit: filters.limit,
      cursor: filters.cursor,
      force: filters.force
    }

    return StrappService.getSIMs(strappFilters)
      .then(response => {
        const sims = response.data || response.results || []
        console.log(`[StrappSimProvider] 📡 Received ${sims.length} SIMs from API`, response)
        const normalizedSims = sims.map(sim => this.normalizeSim(sim))

        return {
          data: normalizedSims,
          cursor: response.cursor,
          remaining: response.remaining,
          total: response.total
        }
      })
  },

  /**
   * Récupère une SIM Strapp par son ICCID
   * @param {string} iccid - ICCID de la SIM
   * @param {boolean} force - Forcer le refresh cache
   * @returns {Promise<Sim>}
   */
  getSimByIccid(iccid, force = false) {
    return this.getSims({ iccid, force })
      .then(result => {
        if (result.data && result.data.length > 0) {
          return result.data[0]
        }
        throw new Error(`SIM with ICCID ${iccid} not found`)
      })
  },

  /**
   * Active une SIM Strapp
   * @param {string} iccid - ICCID de la SIM
   * @param {Object} activationOptions - Options d'activation
   * @returns {Promise<Sim>}
   */
  activateSim(iccid, activationOptions = {}) {
    return this.getSims({ iccid })
      .then(result => {
        if (!result.data || result.data.length === 0) {
          throw new Error(`SIM with ICCID ${iccid} not found`)
        }

        const sim = result.data[0]
        const subscriptionId =
          activationOptions.subscriptionId ||
          sim.raw?.subscriptionId ||
          sim.raw?.subscription?.id ||
          (typeof sim.raw?.subscription === 'string' ? sim.raw.subscription : undefined)

        if (!subscriptionId) {
          throw new Error(`Missing subscriptionId for SIM ${iccid}`)
        }

        const activationData = {
          ...activationOptions,
          simId: sim.raw.id,
          subscriptionId,
          activationMode: activationOptions.activationMode || activationOptions.mode || 'urgent'
        }

        return StrappService.activateSIM(activationData)
      })
      .then(() => this.getSimByIccid(iccid, true))
  },

  /**
   * Désactive une SIM Strapp
   * @param {string} iccid - ICCID de la SIM
   * @param {Object} cancelOptions - Options de résiliation
   * @returns {Promise<Sim>}
   */
  deactivateSim(iccid, cancelOptions = {}) {
    return this.getSims({ iccid })
      .then(result => {
        if (!result.data || result.data.length === 0) {
          throw new Error(`SIM with ICCID ${iccid} not found`)
        }

        const sim = result.data[0]
        const cancelData = {
          simId: sim.raw.id,
          urgent: cancelOptions.urgent !== false,
          ...cancelOptions
        }

        return StrappService.cancelSIM(cancelData)
      })
      .then(() => this.getSimByIccid(iccid, true))
  },

  /**
   * Récupère la consommation d'une SIM
   * @param {string} iccid - ICCID de la SIM
   * @param {Object} options - Options (from, to, aggregation)
   * @returns {Promise<Object>}
   */
  getSimUsage(iccid, options = {}) {
    return StrappService.getSIMUsage({
      iccid,
      ...options
    })
  }
}

export default StrappSimProvider
